/* Partner Management → Packing missing: products in Hub orders with no pills-per-strip in Product Aliases. Set it here;
   "Fix past orders" re-calculates old Hub orders with the strip maths (rate per strip × qty ÷ pills per strip). */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Upload } from "lucide-react";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";

const h = () => ({ headers: getAuthHeader() });
const RANGES = [[1, "Today"], [7, "7 days"], [30, "30 days"], [0, "All"]];
const amt = (n, c) => `${c === "USD" ? "$" : "₹"}${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PackCell = ({ x, onSaved }) => {
  const [v, setV] = useState(x.suggested ? String(x.suggested) : "");
  const save = async () => {
    try { await axios.put(`${API}/portal-admin/packing/${x.alias_id}`, { packing: v }, h()); toast.success(`${x.alias_name}: ${v} per strip`); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  if (!x.alias_id) return <span className="text-[11px] text-rose-600">Not in Product Aliases — add it there first</span>;
  return (
    <div className="flex items-center gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && v && save()} placeholder="e.g. 10" title={x.suggested ? "Suggested from the product name — check it, then Save" : ""}
        className={`h-7 w-20 rounded border px-2 text-xs ${x.suggested && v === String(x.suggested) ? "border-amber-400 bg-amber-50" : ""}`} data-testid={`pk-input-${x.alias_id}`} />
      <Button size="sm" className="h-7 px-2 text-xs" disabled={!v} onClick={save} data-testid={`pk-save-${x.alias_id}`}>Save</Button>
      {x.suggested && v === String(x.suggested) && <span className="text-[10px] text-amber-700">suggested</span>}
    </div>
  );
};

const Recalc = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (apply) => {
    if (apply && !window.confirm(`Change the product totals of ${d.count} past Hub order(s)? Balances and loss marks follow the new totals.`)) return;
    setBusy(true);
    try { const r = await axios.post(`${API}/portal-admin/packing-recalc`, { apply }, h()); setD(apply ? null : r.data); if (apply) toast.success(`${r.data.count} past order(s) updated`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not check"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3 text-xs" data-testid="pk-recalc">
      <div className="flex flex-wrap items-center gap-2">
        <b className="text-indigo-900">Fix past orders</b><span className="text-slate-600">re-calculate old Hub orders with rate per strip × qty ÷ pills per strip</span>
        <Button size="sm" variant="outline" className="ml-auto h-7 text-xs" disabled={busy} onClick={() => run(false)} data-testid="pk-recalc-check">{busy && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}Check</Button>
        {d?.count > 0 && <Button size="sm" className="h-7 text-xs" disabled={busy} onClick={() => run(true)} data-testid="pk-recalc-apply">Update {d.count} order(s)</Button>}
      </div>
      {d && <div className="mt-2" data-testid="pk-recalc-result">
        {!d.count ? <p className="text-emerald-700">All past orders already match the strip maths.</p> : <>
          <p className="font-semibold text-slate-700">{d.count} order(s) change · products total {amt(d.before)} → {amt(d.after)}</p>
          <div className="mt-1 max-h-48 overflow-y-auto">{d.rows.map((x) => <div key={x.id} className="flex gap-2 border-t py-0.5"><span className="w-20">{x.date}</span><span className="flex-1 truncate">{x.partner} · {x.customer}</span><span className="text-rose-600 line-through">{amt(x.old, x.currency)}</span><b className="text-emerald-700">{amt(x.new, x.currency)}</b></div>)}</div>
        </>}
      </div>}
    </div>
  );
};

// Download the packing sheet (missing products, or every Product Alias) and upload it back with Pills per strip filled
export const PackingSheetButtons = ({ scope = "missing", days = 0, onDone }) => {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const download = async () => {
    try {
      const r = await axios.get(`${API}/portal-admin/packing-sheet.xlsx?scope=${scope}&days=${days}`, { ...h(), responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `Packing_${scope}.xlsx`; a.click();
    } catch { toast.error("Download failed"); }
  };
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await axios.post(`${API}/portal-admin/packing-sheet/upload`, fd, h());
      const d = r.data;
      toast.success(`${d.saved} packing(s) saved${d.unmatched_count ? ` · ${d.unmatched_count} not found` : ""}${d.bad_count ? ` · ${d.bad_count} not a number` : ""}`,
        { description: [...d.unmatched, ...d.bad].slice(0, 6).join(" · ") || undefined, duration: 8000 });
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <Button size="sm" variant="outline" className="h-7 gap-1 border-amber-300 text-xs text-amber-800" onClick={download} title="Excel with a Pills per strip column to fill (suggestions included)" data-testid={`pk-sheet-dl-${scope}`}><Download className="h-3.5 w-3.5" />Packing sheet</Button>
      <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`pk-sheet-input-${scope}`} />
      <Button size="sm" variant="outline" className="h-7 gap-1 border-amber-300 text-xs text-amber-800" disabled={busy} onClick={() => ref.current?.click()} data-testid={`pk-sheet-up-${scope}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload packing</Button>
    </>
  );
};

// Product Aliases table: Pills per strip typed straight into the row (Enter / leave the box to save)
export const PackingInline = ({ r, onSaved }) => {
  const [v, setV] = useState(r.packing || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setV(r.packing || ""); }, [r.packing]);
  const save = async () => {
    if (String(v).trim() === String(r.packing || "").trim()) return;
    setBusy(true);
    try { await axios.put(`${API}/portal-admin/packing/${r.id}`, { packing: String(v).trim() }, h()); toast.success(`${r.second_name}: ${v} per strip`); onSaved?.(String(v).trim()); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); setV(r.packing || ""); } finally { setBusy(false); }
  };
  return (
    <input value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} onDoubleClick={(e) => e.stopPropagation()}
      placeholder="—" disabled={busy} title="Pills per strip — type and press Enter"
      className={`h-6 w-14 rounded border px-1 text-center text-[11px] font-semibold ${v ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50"}`} data-testid={`pa-pack-input-${r.id}`} />
  );
};

// ₹/pill under the strip price
export const perPill = (price, packing) => {
  const s = String(packing || "").toUpperCase(), n = s.match(/\d+(\.\d+)?/g);
  const k = !n ? 0 : s.includes("X") && n.length > 1 ? n[0] * n[1] : +n[0], c = parseFloat(String(price || "").replace(/,/g, ""));
  return k > 1 && c > 0 ? `₹${(c / k).toFixed(2)}/pill` : "";
};

// Product Aliases: how many products already have pills per strip
export const PackingProgress = ({ tick }) => {
  const [d, setD] = useState(null);
  useEffect(() => { axios.get(`${API}/portal-admin/packing-progress`, h()).then((r) => setD(r.data)).catch(() => {}); }, [tick]);
  if (!d) return null;
  const tone = d.pct >= 90 ? "bg-emerald-500" : d.pct >= 40 ? "bg-amber-500" : "bg-rose-500";
  return (
    <span className="inline-flex items-center gap-2 rounded-lg border bg-white px-2.5 py-1 text-xs" title="Products with Packing (pills per strip) set" data-testid="pk-progress">
      <b className="text-slate-700">Packing</b>
      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-200"><span className={`block h-full ${tone}`} style={{ width: `${Math.min(100, d.pct)}%` }} /></span>
      <span className="font-mono text-slate-600" data-testid="pk-progress-text">{d.with.toLocaleString("en-IN")} / {d.total.toLocaleString("en-IN")} · {d.pct}%</span>
    </span>
  );
};

const SaveSuggested = ({ rows, onDone }) => {
  const list = rows.filter((x) => x.alias_id && x.suggested);
  if (!list.length) return null;
  const save = async () => {
    if (!window.confirm(`Save the suggested pills per strip for ${list.length} product(s)?\n\n${list.slice(0, 12).map((x) => `${x.alias_name}: ${x.suggested}`).join("\n")}`)) return;
    try { const r = await axios.post(`${API}/portal-admin/packing/bulk`, { items: list.map((x) => ({ alias_id: x.alias_id, packing: String(x.suggested) })) }, h()); toast.success(`${r.data.saved} saved`); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  return <Button size="sm" className="h-7 bg-amber-600 text-xs hover:bg-amber-700" onClick={save} data-testid="pk-save-suggested">Save all suggested ({list.length})</Button>;
};

export const PackingMissingTab = () => {
  const [days, setDays] = useState(1);
  const [d, setD] = useState(null);
  const load = () => { setD(null); axios.get(`${API}/portal-admin/packing-missing?days=${days}`, h()).then((r) => setD(r.data)).catch(() => toast.error("Could not load")); };
  useEffect(load, [days]);
  return (
    <div className="space-y-3" data-testid="pk-admin">
      <p className="text-xs text-slate-500">Hub qty is in pills and rate is per strip. These products were ordered but have no <b>Packing</b> (pills per strip) in Product Aliases, so their order value uses rate × qty and gets an amber “packing missing” tag. Uploads still go through.</p>
      <div className="flex flex-wrap items-center gap-1">
        {RANGES.map(([n, l]) => <button key={n} onClick={() => setDays(n)} className={`rounded-full border px-3 py-1 text-xs font-semibold ${days === n ? "border-amber-500 bg-amber-500 text-white" : "bg-white text-slate-600"}`} data-testid={`pk-range-${n}`}>{l}</button>)}
        <Button size="sm" variant="ghost" className="h-7" onClick={load} data-testid="pk-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
        <span className="ml-auto flex flex-wrap items-center gap-1">
          {d && <SaveSuggested rows={d.rows} onDone={load} />}
          <PackingSheetButtons scope="missing" days={days} onDone={load} />
          {d && <span className="rounded-lg bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="pk-count">{d.count} product(s) need packing</span>}
        </span>
      </div>
      {!d ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-xs"><thead className="bg-amber-50 text-left text-amber-900"><tr><th className="px-2 py-1.5">Product</th><th className="px-2">Orders</th><th className="px-2">Partners</th><th className="px-2">Last order</th><th className="px-2">Pills per strip</th></tr></thead>
            <tbody>{d.rows.map((x) => (
              <tr key={x.alias_id || x.key} className="border-t" data-testid={`pk-row-${x.alias_id || x.key}`}>
                <td className="px-2 py-1.5 font-semibold">{x.medicine}{x.flagged && <span className="ml-1 rounded bg-rose-100 px-1.5 text-[10px] font-semibold text-rose-700" title={`Asked from the Hub by ${(x.flagged_by || []).join(", ")}`} data-testid={`pk-flagged-${x.alias_id || x.key}`}>Partner asked</span>}{x.alias_name && x.alias_name !== x.medicine && <div className="text-[11px] font-normal text-slate-500">{x.alias_name}</div>}</td>
                <td className="px-2">{x.orders}</td><td className="px-2 text-slate-600">{x.partners.join(", ")}</td><td className="px-2 whitespace-nowrap">{x.last}</td>
                <td className="px-2 py-1"><PackCell x={x} onSaved={load} /></td></tr>))}
              {!d.rows.length && <tr><td colSpan={5} className="p-6 text-center text-emerald-700">Every ordered product has its packing.</td></tr>}</tbody></table>
        </div>)}
      <Recalc />
    </div>
  );
};
